import { Injectable } from '@angular/core';
import {
  CertProvider,
  createRegistry,
  DocumentRegistry,
  ecPointFromSpki,
  ImagePixels,
  MJSP_CIN_PUBLIC_KEY,
  ParsedDocument,
  RawSymbol,
  ScanResult,
  SerproCertKey,
  StaticKeyProvider,
  ZxingDecoder,
} from '../../lib/brdocs';
import { browserPhotoDecoder } from './browser-photo';
import { fileToPixels } from './browser-render';
import { SERPRO_CERTS_BUNDLED } from '../../lib/brdocs/verify/serpro-certs.bundled';
import { WorkerQrDecoder } from './worker-qr-decoder';

const MJSP_KEY = 'brdocs.mjsp_jwk';

/**
 * Browser entry point to the brdocs library. Wires the zxing-wasm decoder
 * (self-hosted wasm), the browser photo decoder, and a localStorage-backed
 * key provider for CIN (MJSP) signature verification. The same registry/parsers
 * run unchanged in a Lambda with Node adapters + a server-side key source.
 */
@Injectable({
  providedIn: 'root',
})
export class Scanner {
  /** The MJSP public key (JWK) currently configured, if any. */
  private _mjspKey: JsonWebKey | null = this.loadKey();
  get mjspKey(): JsonWebKey | null {
    return this._mjspKey;
  }

  private readonly registry: DocumentRegistry = createRegistry(
    new ZxingDecoder({ wasmLocation: 'assets/zxing_reader.wasm' }),
    {
      photoDecoder: browserPhotoDecoder,
      keyProvider: new StaticKeyProvider(() => this._mjspKey),
      certProvider: new SerproCertProvider(),
    },
  );

  /** Configure the MJSP public key (JWK) used to verify CIN signatures. */
  setMjspKey(jwk: JsonWebKey | null): void {
    this._mjspKey = jwk;
    if (jwk) {
      localStorage.setItem(MJSP_KEY, JSON.stringify(jwk));
    } else {
      localStorage.removeItem(MJSP_KEY);
    }
  }

  private loadKey(): JsonWebKey | null {
    try {
      const raw = localStorage.getItem(MJSP_KEY);
      // Default to the known MJSP key so CIN signatures verify out of the box.
      return raw ? (JSON.parse(raw) as JsonWebKey) : MJSP_CIN_PUBLIC_KEY;
    } catch {
      return MJSP_CIN_PUBLIC_KEY;
    }
  }

  /**
   * QR decoder that runs the zxing scan in a Web Worker. Used only by the *heavy*
   * one-shot scans ({@link scanPixelsHeavy} / {@link scanFileHeavy}) — a full-
   * resolution document photo. The cheap live-webcam loop keeps using the
   * main-thread {@link scanPixels}, which has no per-frame worker round-trip.
   */
  private readonly workerDecoder = new WorkerQrDecoder();

  /** Scan an uploaded image/PDF file. */
  async scanFile(file: Blob, mrzText?: string): Promise<ScanResult> {
    const pixels = await fileToPixels(file);
    return this.registry.scan(pixels, { mrzText });
  }

  /** Scan a pixel buffer (e.g. a webcam frame). */
  scanPixels(pixels: ImagePixels, mrzText?: string): Promise<ScanResult> {
    return this.registry.scan(pixels, { mrzText });
  }

  /**
   * Heavy scan of a full-resolution document photo: decode the QR **off the main
   * thread** (Web Worker), then parse + verify the signature on the main thread
   * via the shared registry. Behaviourally identical to {@link scanPixels} but it
   * doesn't freeze the UI on a multi-megapixel image. NOTE: the `pixels` buffer is
   * transferred to the worker — do not reuse `pixels` after calling this.
   */
  async scanPixelsHeavy(pixels: ImagePixels, mrzText?: string): Promise<ScanResult> {
    let symbols: RawSymbol[] = [];
    try {
      symbols = await this.workerDecoder.decode(pixels);
    } catch {
      symbols = [];
    }
    return this.registry.scanContext({ symbols, mrzText });
  }

  /** {@link scanPixelsHeavy} for an uploaded image/PDF file. */
  async scanFileHeavy(file: Blob, mrzText?: string): Promise<ScanResult> {
    const pixels = await fileToPixels(file);
    return this.scanPixelsHeavy(pixels, mrzText);
  }

  /** Parse standalone MRZ text (no image). */
  parseMrz(mrzText: string): Promise<ScanResult> {
    return this.registry.scanContext({ symbols: [], mrzText });
  }

  /** Re-run signature verification on a document (e.g. after the key changes). */
  reverify(doc: ParsedDocument): Promise<ParsedDocument> {
    return this.registry.verifyDocument(doc);
  }
}

/**
 * Which path produced the SERPRO candidate certs on the last {@link SerproCertProvider.serproCerts}
 * call. This is the single most useful signal for troubleshooting why a genuine CNH-e can fail field
 * verification: if the live endpoint is blocked (CORS / offline → `TypeError`) we silently fall back
 * to the cache, then to the bundled known-good certs; a stale/empty fallback is what makes real
 * documents come back `unverified`.
 */
export type SerproCertSource =
  | 'live-ok' // live endpoint returned the cert list
  | 'live-failed' // live fetch errored/non-ok AND nothing recovered it (true dead-end)
  | 'cache-hit' // live failed, recovered from the localStorage cache
  | 'bundle-fallback' // live + cache empty, only the hard-coded bundle produced certs
  | 'empty'; // no candidates from any source

/** Diagnostic snapshot of one {@link SerproCertProvider.serproCerts} call. No PII (cert ids are opaque). */
export interface SerproCertSourceDiag {
  /** The path that produced the usable candidate set. */
  source: SerproCertSource;
  /**
   * Name of the error thrown by the live fetch (e.g. `'TypeError'` for a CORS/network failure), or
   * an `HTTP <status>` string for a non-ok response; null when the live fetch succeeded.
   */
  liveError: string | null;
  /** Raw candidate count from the live endpoint (before EC filtering). */
  liveCount: number;
  /** Raw candidate count recovered from the localStorage cache. */
  cacheCount: number;
  /** Raw candidate count folded in from the hard-coded bundle fallback. */
  bundleCount: number;
  /** Final EC-usable cert count handed to the verifier. */
  ecUsableCount: number;
  /** When this diagnostic was recorded (epoch ms). */
  at: number;
}

/**
 * Browser {@link CertProvider} for CNH-e (SERPRO) signature verification. Fetches
 * the live SERPRO certificate list (caching it in localStorage so repeat scans
 * work offline), keeps only the EC keys, and converts each X.509 SPKI key into
 * the uncompressed EC point the verifier expects via `ecPointFromSpki`.
 *
 * Wire it into the registry by adding `certProvider: new SerproCertProvider()`
 * to the `createRegistry` options above.
 */
export class SerproCertProvider implements CertProvider {
  /** SERPRO's public certificate endpoint. */
  static readonly CERTS_URL = 'https://vio.serpro.gov.br/api/v2/certificates';
  private static readonly CACHE_KEY = 'brdocs.serpro_certs';

  /**
   * Diagnostic of the most recent {@link serproCerts} call — see {@link SerproCertSourceDiag}. Static
   * so the KYC provider can read it after a scan (`SerproCertProvider.lastCertSourceDiag`) and surface
   * it into the attempt trace, without threading this provider instance through the capture flow.
   */
  static lastCertSourceDiag: SerproCertSourceDiag | null = null;

  async serproCerts(): Promise<SerproCertKey[]> {
    type RawCert = {
      id: string;
      public_key: string;
      valid?: { from?: string; until?: string };
      group?: { id?: string };
    };
    let raw: RawCert[] | null = null;
    let liveOk = false;
    let liveError: string | null = null;
    let liveCount = 0;
    let cacheCount = 0;
    try {
      const res = await fetch(SerproCertProvider.CERTS_URL);
      if (res.ok) {
        raw = (await res.json()) as RawCert[];
        liveOk = true;
        liveCount = raw.length;
        localStorage.setItem(SerproCertProvider.CACHE_KEY, JSON.stringify(raw));
      } else {
        liveError = `HTTP ${res.status}`;
      }
    } catch (e) {
      // offline / CORS-blocked — capture the error name (e.g. 'TypeError') and fall back to cache.
      liveError = (e as { name?: string })?.name ? String((e as { name?: string }).name) : String(e);
    }
    if (!raw) {
      try {
        const cached = localStorage.getItem(SerproCertProvider.CACHE_KEY);
        const parsed = cached ? (JSON.parse(cached) as RawCert[]) : [];
        cacheCount = parsed.length;
        raw = parsed;
      } catch {
        raw = [];
      }
    }

    // Hard-cache fallback: always fold the bundled known-good certs in as candidates so a genuine
    // CNH-e still verifies even when the live fetch failed and localStorage was empty. Fresh certs
    // win (bundle is purely additive) — only append bundled entries whose id isn't already present.
    let bundleCount = 0;
    const seenIds = new Set(raw.map((c) => c.id));
    for (const b of SERPRO_CERTS_BUNDLED) {
      if (!seenIds.has(b.id)) {
        raw.push({
          id: b.id,
          public_key: b.public_key,
          valid: b.valid ?? undefined,
          group: b.group ?? undefined,
        });
        seenIds.add(b.id);
        bundleCount++;
      }
    }

    const certs: SerproCertKey[] = [];
    for (const c of raw) {
      const der = Uint8Array.from(atob(c.public_key), (ch) => ch.charCodeAt(0));
      if (der.length >= 256) {
        continue; // RSA key — CNH-e signatures are EC
      }
      const point = ecPointFromSpki(der);
      if (point) {
        certs.push({ id: c.id, publicKey: point, valid: c.valid, groupId: c.group?.id });
      }
    }

    // Classify the path that produced the candidate set, freshest-source-first.
    const source: SerproCertSource = liveOk
      ? 'live-ok'
      : cacheCount > 0
      ? 'cache-hit'
      : bundleCount > 0
      ? 'bundle-fallback'
      : liveError
      ? 'live-failed'
      : 'empty';
    SerproCertProvider.lastCertSourceDiag = {
      source,
      liveError,
      liveCount,
      cacheCount,
      bundleCount,
      ecUsableCount: certs.length,
      at: Date.now(),
    };
    return certs;
  }
}
